import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, Phone, Instagram, Facebook, Tag } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { useCart } from '../context/CartContext';
import { cachedFetch } from '../lib/sanity';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const ADMIN_URL = (import.meta as any).env.DEV 
  ? 'http://localhost:3333' 
  : 'https://multimack-panel.sanity.studio';

const NAV_LINKS = [
  { name: 'Inicio', href: '/' },
  { 
    name: 'Productos', 
    href: '/productos',
    prefetch: () => {
      cachedFetch(`*[_type == "product"]{ ..., "categoryName": category->title }`);
      cachedFetch(`*[_type == "category" && count(*[_type == "product" && references(^._id)]) > 0]`);
    }
  },
  { 
    name: 'Ofertas', 
    href: '/ofertas',
    prefetch: () => {
      cachedFetch(`*[_id == "offers-content"][0]`);
    }
  },
  { name: 'Sectores', href: '/sectores' },
  { name: 'Blog', href: '/blog' },
  { name: 'Nosotros', href: '/nosotros' },
  { name: 'Contacto', href: '/contacto' },
];

export const Navbar: React.FC<{ onOpenCart: () => void }> = ({ onOpenCart }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { totalItems } = useCart();

  const [activeInfo, setActiveInfo] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Timer for rotating top bar info on mobile
    const timer = setInterval(() => {
      setActiveInfo((prev) => (prev === 0 ? 1 : 0));
    }, 4000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(timer);
    };
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
      {/* Top Bar */}
      <div className={cn(
        "bg-brand-dark-blue text-brand-white px-6 transition-all duration-300 overflow-hidden",
        scrolled ? "h-0 opacity-0" : "h-auto opacity-100"
      )}>
        <div className="max-w-7xl mx-auto text-[11px] sm:text-[12px] font-bold uppercase tracking-widest">
          {/* Desktop Version */}
          <div className="hidden sm:flex items-center justify-between py-1">
            <div className="flex items-center gap-8">
              <a href="tel:+56948758084" className="flex items-center gap-2 hover:text-brand-green transition-colors whitespace-nowrap py-2" aria-label="Llamar al +56 9 4875 8084">
                <Phone size={14} className="text-brand-green" aria-hidden="true" />
                +56 9 4875 8084
              </a>
              <div className="flex items-center gap-6 border-l border-brand-white/20 pl-6">
                <a href="https://www.instagram.com/envases_multimack?igsh=MTY4eHBnNTF1cHZzZw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="hover:text-brand-green transition-colors p-2" aria-label="Visitar nuestro Instagram"><Instagram size={16} aria-hidden="true" /></a>
                <a href="https://www.facebook.com/multimack.los.libertadores.2025" target="_blank" rel="noopener noreferrer" className="hover:text-brand-green transition-colors p-2" aria-label="Visitar nuestro Facebook"><Facebook size={16} aria-hidden="true" /></a>
              </div>
            </div>
            
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <Link to="/ofertas" className="flex items-center gap-2 text-brand-gold hover:text-brand-green transition-colors whitespace-nowrap py-2" aria-label="Ver ofertas especiales">
                <Tag size={14} />
                ¡Ver Ofertas Especiales!
              </Link>
            </motion.div>
          </div>

          {/* Mobile Version (Rotating) */}
          <div className="sm:hidden h-10 relative overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeInfo}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="absolute inset-0 flex items-center justify-center"
              >
                {activeInfo === 0 ? (
                  <a href="tel:+56948758084" className="flex items-center gap-2 py-2" aria-label="Llamar al +56 9 4875 8084">
                    <Phone size={14} className="text-brand-green" aria-hidden="true" />
                    +56 9 4875 8084
                  </a>
                ) : (
                  <Link to="/ofertas" className="flex items-center gap-2 text-brand-gold py-2" aria-label="Ver ofertas especiales">
                    <Tag size={14} />
                    ¡Ver Ofertas Especiales!
                  </Link>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Main Bar */}
      <div className={cn(
        'transition-all duration-300 px-6 py-4',
        scrolled ? 'bg-brand-white/90 backdrop-blur-md shadow-md py-3' : 'bg-brand-white/50 backdrop-blur-sm'
      )}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center group" 
            aria-label="Ir al inicio de MULTIMACK"
            onClick={() => {
              setIsOpen(false);
              if (location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <img 
              src="/images/logo.webp" 
              alt="Logo MULTIMACK" 
              className="h-12 md:h-16 w-auto object-contain transition-transform group-hover:scale-105" 
              width="240"
              height="64"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onMouseEnter={() => (link as any).prefetch?.()}
                className={cn(
                  'text-sm font-semibold transition-colors hover:text-brand-green-dark',
                  location.pathname === link.href ? 'text-brand-green-dark' : 'text-brand-dark-blue'
                )}
                aria-current={location.pathname === link.href ? 'page' : undefined}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Cart Button */}
            <button 
              onClick={onOpenCart}
              className="relative p-2 text-brand-dark-blue hover:text-brand-green transition-colors"
              aria-label={`Ver carrito (${totalItems} productos)`}
            >
              <ShoppingBag size={24} />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-green text-brand-dark-blue text-[10px] font-black w-5 h-5 flex items-center justify-center rounded-full border-2 border-brand-white">
                  {totalItems}
                </span>
              )}
            </button>

            {/* Admin Access (Hidden in plain sight) */}
            <a 
              href={ADMIN_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 text-brand-dark-blue/20 hover:text-brand-green transition-colors"
              aria-label="Acceso Administración"
              title="Admin"
            >
              <Tag size={14} className="rotate-90" />
            </a>

            <a
              href="https://wa.me/56948758084?text=Hola,%20quiero%20recibir%20una%20cotizaci%C3%B3n%20personalizada"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary py-2 px-4 text-sm"
              aria-label="Cotizar por WhatsApp"
            >
              <Phone size={16} />
              Cotizar
            </a>
          </div>

          {/* Mobile Toggle */}
          <div className="flex items-center gap-4 md:hidden">
            <button 
              onClick={onOpenCart}
              className="relative p-2 text-brand-dark-blue"
              aria-label={`Ver carrito (${totalItems} productos)`}
            >
              <ShoppingBag size={24} />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-green text-brand-dark-blue text-[10px] font-black w-5 h-5 flex items-center justify-center rounded-full border-2 border-brand-white">
                  {totalItems}
                </span>
              )}
            </button>
            <button 
              className="text-brand-dark-blue p-2" 
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Cerrar menú" : "Abrir menú"}
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-full left-0 right-0 bg-brand-white shadow-xl p-6 flex flex-col gap-4 md:hidden max-h-[80vh] overflow-y-auto"
          >
            {NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => setIsOpen(false)}
                className={cn(
                  'text-lg font-semibold py-2 border-b border-brand-black/5',
                  location.pathname === link.href ? 'text-brand-green' : 'text-brand-dark-blue'
                )}
              >
                {link.name}
              </Link>
            ))}
            <a
              href="https://wa.me/56948758084?text=Hola,%20quiero%20recibir%20una%20cotizaci%C3%B3n%20personalizada"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-4"
              aria-label="Cotizar por WhatsApp"
            >
              <Phone size={18} aria-hidden="true" />
              Cotizar por WhatsApp
            </a>

            {/* Hidden Admin Link for Mobile */}
            <a 
              href={ADMIN_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[10px] text-brand-dark-blue/20 text-center mt-4"
            >
              Acceso Admin
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

